import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { sessionBelongsToLots } from '../sessionScope';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AddParkingLotDialog from '../components/AddParkingLotDialog';
import EditParkingLotDialog from '../components/EditParkingLotDialog';

interface ParkingLot {
  id: string;
  lotCode?: string;
  lotName: string;
  address: string;
  ratePeriod?: string;
  currencies?: Record<string, { rate?: number | null }>;
  maxOvertimeHours?: number | null;
  ratePerHour?: number;
  availability: number;
  totalSpots: number;
  location?: any;
  vendorId?: string;
  paymentTiming?: string;
  accessType?: string;
  flatFeeDuration?: number;
  flatFee?: number;
  dailyMaxCap?: number;
  pricingType?: 'FLAT_FEE' | 'PER_HOUR';
  // Approval state. Missing field === legacy lot, treated as approved.
  status?: 'pending_review' | 'approved' | 'rejected';
}

export default function ParkingLocations() {
  const [lots, setLots] = useState<ParkingLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLot, setSelectedLot] = useState<ParkingLot | null>(null);

  const fetchLots = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'parkinglots'));
      // Operational list — only approved (or legacy missing-status) lots.
      // Pending and rejected lots live on the Pending Approvals page.
      const lotsData = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }) as ParkingLot)
        .filter(l => !l.status || l.status === 'approved');
      setLots(lotsData);
    } catch (error) {
      console.error('Error fetching parking lots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedLot) return;

    // Refuse outright while parkers are on the lot. The session keeps
    // running either way, but deleting the lot severs the doc it points at
    // mid-stay — nothing downstream can resolve its rate, timezone or code.
    try {
      const activeSnapshot = await getDocs(query(
        collection(db, 'parkingsessions'),
        where('sessionActive', '==', true)
      ));
      const activeHere = activeSnapshot.docs.filter((d) =>
        sessionBelongsToLots(
          d.data() as any,
          new Set([selectedLot.id]),
          new Set([selectedLot.lotName])
        )
      ).length;

      if (activeHere > 0) {
        alert(
          `"${selectedLot.lotName}" still has ${activeHere} active session` +
            `${activeHere === 1 ? '' : 's'}.\n\n` +
            'End those sessions first, or use "Send Back to Pending Approval" ' +
            'to take the lot offline without deleting it.'
        );
        return;
      }
    } catch (error: any) {
      alert('Could not check for active sessions, so the lot was not deleted: ' + error.message);
      return;
    }

    // Two-step confirmation because delete is unrecoverable. Lot codes
    // are scarce (2-letter prefix) and active sessions snapshot lot
    // data, so the deletion only affects future bookings — but the
    // historical link from session → lot doc is severed.
    if (
      !window.confirm(
        `Delete "${selectedLot.lotName}"?\n\n` +
          'This cannot be undone. Active sessions on this lot will keep ' +
          "running (they don't depend on the lot doc), but the lot can " +
          'never be booked again under this code.'
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        `Final confirmation: permanently delete "${selectedLot.lotName}" (${selectedLot.lotCode || 'no code'})?`
      )
    ) {
      return;
    }
    try {
      await deleteDoc(doc(db, 'parkinglots', selectedLot.id));
      setEditDialogOpen(false);
      setSelectedLot(null);
      fetchLots();
    } catch (error) {
      console.error('Error deleting lot:', error);
      alert('Failed to delete parking lot');
    }
  };

  const handleSendBackToPendingSelected = async () => {
    if (!selectedLot) return;
    if (
      !window.confirm(
        `Send "${selectedLot.lotName}" back to Pending Approval?\n\n` +
          'It will stop being bookable until you re-approve it. ' +
          'Active sessions on the lot are unaffected.'
      )
    ) {
      return;
    }
    try {
      await updateDoc(doc(db, 'parkinglots', selectedLot.id), {
        status: 'pending_review',
        // Clear the prior approval audit fields so the partner-visible
        // history doesn't read as "approved by X, also pending."
        approvedAt: null,
        approvedBy: null,
        // Trail of who unapproved when, for our own bookkeeping.
        sentBackAt: serverTimestamp(),
        sentBackBy: auth.currentUser?.email || null,
      });
      setEditDialogOpen(false);
      setSelectedLot(null);
      fetchLots();
    } catch (error) {
      console.error('Error sending lot back to pending:', error);
      alert('Failed to send lot back to Pending Approvals.');
    }
  };

  useEffect(() => {
    fetchLots();
  }, []);

  const getPricingDisplay = (lot: ParkingLot) => {
    if (lot.currencies && Object.keys(lot.currencies).length > 0) {
      return Object.entries(lot.currencies)
        .map(([code, pricing]) => `${code} ${pricing.rate ?? 'N/A'}/${lot.ratePeriod || 'hour'}`)
        .join(', ');
    }
    if (lot.pricingType === 'FLAT_FEE' && lot.flatFee != null) {
      const hrs = lot.flatFeeDuration ? `${lot.flatFeeDuration / 60} hrs` : 'flat';
      return `$${lot.flatFee.toFixed(2)} (${hrs})`;
    }
    if (lot.ratePerHour != null) {
      const max = lot.dailyMaxCap != null ? ` (max $${lot.dailyMaxCap.toFixed(2)}/day)` : '';
      return `$${lot.ratePerHour.toFixed(2)}/hr${max}`;
    }
    return 'N/A';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 1,
        mb: 2
      }}>
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Parking Locations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          size="small"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add New Lot
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>Lot Code</TableCell>
              <TableCell>Lot Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Pricing</TableCell>
              <TableCell>Available Spots</TableCell>
              <TableCell>Partner</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lots.map((lot) => (
              <TableRow key={lot.id}>
                <TableCell>
                  <strong style={{ fontSize: '1.2em', fontFamily: 'monospace' }}>
                    {lot.lotCode || 'N/A'}
                  </strong>
                </TableCell>
                <TableCell>{lot.lotName}</TableCell>
                <TableCell>{lot.address}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {getPricingDisplay(lot)}
                  </Typography>
                </TableCell>
                <TableCell>{lot.availability}</TableCell>
                <TableCell>{lot.vendorId || 'Unassigned'}</TableCell>
                <TableCell>
                  <Tooltip title="Edit lot details (delete and approval rollback live inside)">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedLot(lot);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <AddParkingLotDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={fetchLots}
      />

      <EditParkingLotDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedLot(null);
        }}
        lot={selectedLot}
        adminMode
        onSuccess={fetchLots}
        onDelete={handleDeleteSelected}
        onSendBackToPending={handleSendBackToPendingSelected}
      />
    </Box>
  );
}